// The subscription journeys, as the browser sees them.
//
// One module for the six addresses this phase adds under /portal/api/, and one
// convention throughout: the server sends raw values and this file does not
// change them. Timestamps arrive as ISO-8601 strings and stay strings until a
// component decides how to show one; a state arrives as both a machine value
// and the label the server-rendered pages use for it, and the screens read the
// label so the two surfaces cannot come to disagree.
//
// Every call goes through the session's `authorizedFetch`, which bears the
// access token and renews it once if it has aged out. No screen here reads a
// token.

/** What the server says a refusal was. Mirrors PortalRequestFailed's job. */
export class PortalDataFailed extends Error {
  readonly status: number;

  /** Per-field problems, where the endpoint refused a form rather than a request. */
  readonly problems: Record<string, string[]>;

  constructor(status: number, message: string, problems: Record<string, string[]> = {}) {
    super(message);
    this.name = 'PortalDataFailed';
    this.status = status;
    this.problems = problems;
  }
}

/** A subscription, projected onto what its model lets a customer see. */
export interface Subscription {
  public_id: string;
  channel: string;
  agreement_ref: string;
  state: string;
  started_at: string | null;
  ended_at: string | null;
  /** How the server-rendered pages name this state. */
  state_label: string;
  /** Which of running / waiting / over it belongs to: is-active, is-waiting, is-over. */
  state_class: string;
}

export interface Customer {
  public_id: string;
  display_name: string;
  primary_contact_email: string;
  created_at: string;
}

export interface Instance {
  public_id: string;
  name: string;
  client_id: string;
  state: string;
  created_at: string;
}

export interface ProvisioningRequest {
  public_id: string;
  region: string;
  instance_name: string;
  /**
   * The address the customer named for the deployment's first login, read
   * back as they gave it.
   *
   * Empty on a request filed before the form began asking — never `null`,
   * because the backend records "nobody was asked" as an empty column rather
   * than an absent one, and the two would otherwise be a distinction this app
   * has to invent a meaning for.
   */
  initial_user_email: string;
  decision_pack: string;
  state: string;
  note: string;
  requested_at: string;
}

export interface Cancellation {
  public_id: string;
  reason: string;
  retention_statement: string;
  wind_down_not_before: string;
  requested_at: string;
}

/** A form field as the server describes it. Labels come from the Django form. */
export interface FieldDescriptor {
  name: string;
  label: string;
  required: boolean;
  help_text: string;
  max_length: number | null;
  multiline: boolean;
  /**
   * The answers the server will accept, where it accepts a closed set — and an
   * EMPTY array, never a missing key, where the field is free text. A caller
   * can therefore ask `choices.length` without first asking whether there are
   * any, which is why this is not optional.
   *
   * Both halves are strings because the server reads them straight off the
   * form field it validates against, so an option offered here is an option
   * the server takes. `value` is what goes back on submit; `label` is only for
   * the person choosing, and this file does not parse or reformat either — the
   * `· us-east-1` on the end of a region label is the server's wording, not a
   * pattern to pick apart.
   *
   * Nothing here says "draw a dropdown". That stays the app's call, the same
   * way `multiline` says what the field IS and the screen decides that what it
   * is means a text area.
   */
  choices: { value: string; label: string }[];
  /**
   * What the server proposes the field start at — the asking member's own
   * address, for the deployment's first login — or an EMPTY STRING where it
   * proposes nothing. Never a missing key, so a caller reads it the way it
   * reads `choices`.
   *
   * A value, not a rule: this app puts it in the box and has no idea whose
   * address it is or why the server thinks it fits. It is also only a
   * STARTING point — the person types over it when they are provisioning for
   * a colleague, and what they leave is what goes up.
   */
  initial: string;
}

export interface SubscriptionList {
  customer: Customer;
  subscriptions: Subscription[];
}

export interface SubscriptionStatus {
  subscription: Subscription;
  customer: Customer;
  instances: Instance[];
  provisioning_requests: ProvisioningRequest[];
  cancellation: Cancellation | null;
}

export interface ProvisioningForm {
  subscription: Subscription;
  fields: FieldDescriptor[];
}

export interface CancellationFlow {
  subscription: Subscription;
  cancellation: Cancellation | null;
  /** What the customer is told about their data, verbatim. */
  retention_statement: string;
  wind_down: {
    not_before: string;
    flush_window_seconds: number;
    /** Whether the channel declared the window, or this is the default allowance. */
    declared_by_channel: boolean;
    /** The deployments that wind down, named as the customer knows them. */
    instances: { public_id: string; name: string }[];
  };
  fields: FieldDescriptor[];
}

export interface Person {
  email: string;
  /** What they gave when they made the account. Empty where they gave none. */
  name: string;
  joined: string;
  is_you: boolean;
}

export interface People {
  customer: Customer;
  people: Person[];
}

export interface Account {
  user: { email: string; name: string; joined: string };
  customer: Customer;
  membership: { joined: string | null };
  name_limit: number;
  /** Where the portal sends somebody for the flows allauth owns. */
  elsewhere: {
    password_change: string;
    password_reset: string;
    email_addresses: string;
  };
}

/** The session's fetch, narrowed to what this module needs of it. */
export type AuthorizedFetch = (path: string, init?: RequestInit) => Promise<Response>;

const BASE = '/portal/api';

export function subscriptionsPath(): string {
  return `${BASE}/subscriptions/`;
}

export function statusPath(publicId: string): string {
  return `${BASE}/subscriptions/${encodeURIComponent(publicId)}/`;
}

export function provisioningPath(publicId: string): string {
  return `${BASE}/subscriptions/${encodeURIComponent(publicId)}/provisioning/`;
}

export function endPath(publicId: string): string {
  return `${BASE}/subscriptions/${encodeURIComponent(publicId)}/end/`;
}

export async function fetchSubscriptions(send: AuthorizedFetch): Promise<SubscriptionList> {
  return read<SubscriptionList>(await send(subscriptionsPath()));
}

export async function fetchStatus(
  send: AuthorizedFetch,
  publicId: string,
): Promise<SubscriptionStatus> {
  return read<SubscriptionStatus>(await send(statusPath(publicId)));
}

export async function fetchProvisioningForm(
  send: AuthorizedFetch,
  publicId: string,
): Promise<ProvisioningForm> {
  return read<ProvisioningForm>(await send(provisioningPath(publicId)));
}

export async function submitProvisioningRequest(
  send: AuthorizedFetch,
  publicId: string,
  values: Record<string, string>,
): Promise<void> {
  await read(await send(provisioningPath(publicId), postingJson(values)));
}

export async function fetchCancellationFlow(
  send: AuthorizedFetch,
  publicId: string,
): Promise<CancellationFlow> {
  return read<CancellationFlow>(await send(endPath(publicId)));
}

export async function fetchPeople(send: AuthorizedFetch): Promise<People> {
  return read<People>(await send(`${BASE}/people/`));
}

export async function fetchAccount(send: AuthorizedFetch): Promise<Account> {
  return read<Account>(await send(`${BASE}/account/`));
}

export async function saveAccountName(send: AuthorizedFetch, name: string): Promise<void> {
  await read(await send(`${BASE}/account/`, postingJson({ name })));
}

function postingJson(payload: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  };
}

/**
 * The body, or the refusal as an error carrying whatever the server said.
 *
 * A 400 from these endpoints is a refused form and arrives with `problems`
 * keyed by field name, because the Django form that refused it is the same one
 * the server told this app to draw. A screen shows those beside the inputs
 * rather than inventing its own validation to duplicate them.
 */
async function read<T>(response: Response): Promise<T> {
  if (response.ok) {
    return (await response.json()) as T;
  }

  let message = `The service answered ${response.status}`;
  let problems: Record<string, string[]> = {};
  try {
    const failure = (await response.json()) as {
      error?: string;
      problems?: Record<string, string[]>;
    };
    if (failure.error) {
      message = failure.error.charAt(0).toUpperCase() + failure.error.slice(1);
    }
    if (failure.problems) {
      problems = failure.problems;
    }
  } catch {
    // A refusal with no JSON body — a proxy answering instead of the service.
  }
  throw new PortalDataFailed(response.status, message, problems);
}
